/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/hotDeskingApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Hot Desking module - all API calls centralized here.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { HotDeskDevice, HotDeskDevicePayload } from '../types';

export interface ListParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface ListResponse {
  data: HotDeskDevice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetches list of hot desk devices with pagination and filters
 */
export async function fetchHotDeskingDevices(params: ListParams): Promise<ListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking?${queryParams}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch hot desking devices');
  }

  const result = await response.json();
  return {
    data: result.data || [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * Fetches a single hot desk device by ID
 */
export async function fetchHotDeskingDeviceById(id: string | number): Promise<HotDeskDevice> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch hot desking device');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Creates a new hot desk device
 */
export async function createHotDeskingDevice(data: HotDeskDevicePayload): Promise<HotDeskDevice> {
  const response = await fetch(
    buildApiUrl('/api/telephony/hot-desking'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create hot desking device' }));
    throw new Error(errorData.message || 'Failed to create hot desking device');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Updates an existing hot desk device
 */
export async function updateHotDeskingDevice(id: string | number, data: HotDeskDevicePayload): Promise<HotDeskDevice> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update hot desking device' }));
    throw new Error(errorData.message || 'Failed to update hot desking device');
  }

  const result = await response.json();
  // Handle cases where API returns { data: {...} } or {...} directly
  // If no data is returned (update case), return the original data with the ID
  return result.data || result || { ...data, id: id as string };
}

/**
 * Deletes a hot desk device by ID
 */
export async function deleteHotDeskingDevice(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete hot desk device';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Fetches device XML for a given device ID
 */
export async function fetchHotDeskingDeviceXml(id: string | number): Promise<string> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking/${id}/xml`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.text();
}


